import React, {useState, useRef} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import moment from "moment";
import {
    Form,
    Input,
    Select,
    Dropdown,
    Menu,
    Modal,
    Tooltip,
    DatePicker,
    Row,
    Col,
    Popconfirm
} from "antd";
import {EllipsisOutlined, HomeOutlined, AimOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import placeholder from "../../../public/images/person-placeholder.jpg";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {ImageEditorModal} from "../../_defaultComponents/ImageEditorModal/ImageEditorModal";
import {newCourierPropType} from "./newCourierPropTypes";
import {ImgBg, IconContainer, Img, GeolocateButton} from "./newCourierStyles";
import {
    courierTypes,
    cities as cityLocations,
    vehicleOwnershipTypes,
    bloodTypes,
    courierEmploymentTypes,
    providerCompanies,
    managerCompanyOptions
} from "../../../constants/constants";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import locale from "antd/es/date-picker/locale/tr_TR";
import CityInput from "./cityInput/CityInput";
import DistrictInput from "./districtInput/DistrictInput";
import {useNewCourier} from "./hooks/useNewCourier";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";

export const NewCourier = ({courierId}: newCourierPropType) => {
    const stores = useStore();
    const [workingModelsLoading, workingModelsData] = useFetch({
        defaultDataValue: {result: []},
        fetchFn: stores.courierStore.listWorkingModels.bind(null, {isActive: true}),
        initialCall: true
    });
    const workingModels = workingModelsData?.result || [];
    const history = useHistory();
    const {t, i18n} = useTranslation(["newCourier"]);
    const [editorVisible, setEditorVisible] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [criminalCheck, setCriminalCheck] = useState(!!courierId);
    const [loadingCourier, courier = {}, setCourier, setLoadingCourier] = useFetch({
        fetchFn: stores.courierStore.getCourier.bind(null, {courierId}),
        showLoading: true,
        condition: !!courierId
    });

    const {
        formik,
        geolocateCourierHomeLocation,
        renderFormInput,
        renderFormSelect,
        renderMaskedInput,
        renderTextArea,
        renderCheckbox,
        renderDatePicketField,
        renderSwitch,
        renderLicensePlateInput,
        setCourierPhoto,
        setMapZoom,
        setMapViewCenter,
        mapZoom,
        mapViewCenter,
        geolocationLoading
    } = useNewCourier({courier, courierId, setLoadingCourier});

    const {
        values,
        errors,
        touched,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched
    } = formik;

    // Sağlayıcı firma yalnızca İK ve admin rolleri tarafından değiştirilebilir
    const canEditProviderCompany = stores.meStore.checkRole("editProviderCompany");

    const [hakedisConfirmVisible, setHakedisConfirmVisible] = useState(false);
    const [checkMessage, setCheckMessage] = useState("");
    const providerPayloadRef = useRef({});

    // Sağlayıcı firma yalnızca dedike uçtan kaydedilir (updateCourier bu alanı düşürür).
    // Kaydet'te önce /provider-company/check çağrılır; hasProcessedRecords (Vion) ise backend'in
    // döndüğü mesaj popconfirm'de gösterilir, onayda /provider-company (PUT) güncellenir,
    // ardından formun kalanı normal submit ile kaydedilir.
    const [, , , , runProviderUpdate] = useFetch({
        fetchFn: () => stores.courierStore.updateProviderCompany(providerPayloadRef.current),
        initialCall: false,
        callbackFunc: ok => {
            if (ok) handleSubmit();
        }
    });

    const [, , , , runProviderCheck] = useFetch({
        fetchFn: () => stores.courierStore.checkProviderCompany(providerPayloadRef.current),
        initialCall: false,
        callbackFunc: result => {
            if (!result) return; // hata zaten error modal ile gösterildi
            if (result.hasProcessedRecords) {
                setCheckMessage(result.message);
                setHakedisConfirmVisible(true);
            } else {
                runProviderUpdate();
            }
        }
    });

    const handleSaveClick = async () => {
        // Düzenleme modunda sağlayıcı firma seçiliyse (Vion olsun olmasın) dedike uçtan
        // kaydedilir: check -> gerekiyorsa popconfirm -> provider-company update, sonra formun kalanı.
        if (canEditProviderCompany && courierId && values.providerCompany) {
            const validationErrors = await formik.validateForm();
            if (Object.keys(validationErrors).length > 0) {
                handleSubmit(); // hataları göster
                return;
            }
            providerPayloadRef.current = {
                courierId,
                providerCompany: values.providerCompany,
                transitionDate: values.vionTransitionDate
                    ? moment(values.vionTransitionDate).format("YYYY-MM-DD")
                    : undefined
            };
            runProviderCheck();
            return;
        }
        handleSubmit();
    };

    return useObserver(() => (
        <VFlex justify="center" data-testid="newCourierScreen">
            <ImageEditorModal
                visible={editorVisible}
                setVisible={value => setEditorVisible(value)}
                setStatePhoto={img => setCourierPhoto(img)}
                setPhoto={photoId => setFieldValue("photo", photoId)}
            />
            <Modal
                visible={showImage}
                onCancel={() => setShowImage(false)}
                footer={null}
                data-testid="profilePhotoModal"
            >
                <VFlex justify="center">
                    <Img src={values.photo || placeholder} data-testid="profilePhoto" />
                </VFlex>
            </Modal>
            <VFlex vertical data-testid="newCourierFormCard">
                <Form
                    autoComplete="off"
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                    onFinish={handleSubmit}
                    data-testid="newCourierForm"
                >
                    <ErrorFocus formik={formik} />
                    <VFlex justify="center">
                        <ImgBg
                            isEdit={!!courierId}
                            onClick={courierId ? () => {} : () => setEditorVisible(true)}
                            src={values.photo || placeholder}
                            data-testid="profilePhoto"
                        >
                            {courierId && (
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            {values.photo && (
                                                <Menu.Item
                                                    onClick={() => setShowImage(true)}
                                                    data-testid="profilePhoto_open"
                                                >
                                                    {t("actions.open", {ns: "common"})}
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                onClick={() => setEditorVisible(true)}
                                                data-testid="profilePhoto_update"
                                            >
                                                {t("actions.update", {ns: "common"})}
                                            </Menu.Item>
                                        </Menu>
                                    }
                                >
                                    <IconContainer>
                                        <EllipsisOutlined />
                                    </IconContainer>
                                </Dropdown>
                            )}
                            {!values.photo && !courierId && (
                                <CustomButton data-testid="profilePhoto_add">
                                    {t("actions.addImage", {ns: "common"})}
                                </CustomButton>
                            )}
                        </ImgBg>
                    </VFlex>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormInput("courierName", "fullname", 11, true)}
                        </Col>

                        <Col xs={24} md={12}>
                            {renderFormSelect(
                                "courierType",
                                "type",
                                courierTypes,
                                "value",
                                "name",
                                false,
                                11,
                                true,
                                true
                            )}
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="İsim"
                                required={true}
                                validateStatus={errors.name && touched.name ? "error" : "success"}
                                help={errors.name && touched.name ? errors.name : null}
                                data-testid="nameInput"
                            >
                                <Input
                                    name="name"
                                    value={values.name}
                                    onChange={e => setFieldValue("name", e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Soyisim"
                                required={true}
                                validateStatus={
                                    errors.surname && touched.surname ? "error" : "success"
                                }
                                help={errors.surname && touched.surname ? errors.surname : null}
                                data-testid="surnameInput"
                            >
                                <Input
                                    name="surname"
                                    value={values.surname}
                                    onChange={e => setFieldValue("surname", e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderSwitch("drivingTraining", "trained", 11)}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderSwitch("testUser", "isTestUser", 11)}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormSelect(
                                "gender",
                                "gender",
                                ["male", "female"],
                                null,
                                null,
                                false,
                                11,
                                true,
                                true
                            )}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderFormSelect(
                                "bloodGroup",
                                "bloodGroup",
                                bloodTypes,
                                null,
                                null,
                                false,
                                11,
                                false,
                                false
                            )}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormInput("identificationNumber", "tckn", 11, true)}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderDatePicketField("birthDate", "birthDate", true)}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderMaskedInput("phone", "cellPhone", 11, true)}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderFormInput("email", "email", 11, true)}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Çalışmak İstenen İl"
                                required={true}
                                validateStatus={
                                    errors.preferredCityId && touched.preferredCityId
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.preferredCityId && touched.preferredCityId
                                        ? errors.preferredCityId
                                        : null
                                }
                                data-testid="preferredCitySelect"
                            >
                                <CityInput
                                    value={values.preferredCityId}
                                    onChange={value => {
                                        setFieldValue("preferredCityId", value);
                                        setFieldValue("preferredDistrictIds", []);
                                    }}
                                    optionMappingKey="_id"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Çalışmak İstenen İlçe(ler)"
                                required={true}
                                validateStatus={
                                    errors.preferredDistrictIds && touched.preferredDistrictIds
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.preferredDistrictIds && touched.preferredDistrictIds
                                        ? errors.preferredDistrictIds
                                        : null
                                }
                                data-testid="preferredDistrictSelect"
                            >
                                <DistrictInput
                                    value={values.preferredDistrictIds}
                                    onChange={value => setFieldValue("preferredDistrictIds", value)}
                                    selectedCityId={values.preferredCityId}
                                    optionMappingKey="_id"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Şehir"
                                required={true}
                                validateStatus={errors.city && touched.city ? "error" : "success"}
                                help={errors.city && touched.city ? errors.city : null}
                                data-testid="citySelect"
                            >
                                <CityInput
                                    value={values.city}
                                    optionMappingKey="name"
                                    onChange={value => {
                                        const cityValue =
                                            value?.toLocaleLowerCase("tr-TR") || undefined;
                                        setFieldValue("city", cityValue);
                                        setFieldValue("county", undefined);

                                        if (cityValue) {
                                            const selectedCity = cityLocations.find(
                                                city => city.value === cityValue
                                            );
                                            if (selectedCity && selectedCity.center) {
                                                setMapZoom(selectedCity.zoom || 10);
                                                setMapViewCenter({
                                                    lat: selectedCity.center.lat,
                                                    lng: selectedCity.center.lng
                                                });
                                                if (
                                                    !values.homeLocation?.latitude ||
                                                    !values.homeLocation?.longitude
                                                ) {
                                                    setFieldValue("homeLocation", {
                                                        latitude: selectedCity.center.lat,
                                                        longitude: selectedCity.center.lng
                                                    });
                                                }
                                            }
                                        } else {
                                            setMapViewCenter(null);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="İlçe"
                                required={true}
                                validateStatus={
                                    errors.county && touched.county ? "error" : "success"
                                }
                                help={errors.county && touched.county ? errors.county : null}
                                data-testid="countySelect"
                            >
                                <DistrictInput
                                    value={values.county}
                                    mode="single"
                                    onChange={async value => {
                                        const countyValue =
                                            value?.toLocaleLowerCase("tr-TR") || undefined;
                                        setFieldValue("county", countyValue);

                                        if (countyValue && values.city) {
                                            try {
                                                const addresses = await stores.locationStore.listAutocompleteAddresses(
                                                    {
                                                        address: `${countyValue}, ${values.city}`,
                                                        components: "country:tr"
                                                    }
                                                );

                                                if (addresses && addresses.length > 0) {
                                                    const locationDetail = await stores.locationStore.getLocationDetail(
                                                        {
                                                            placeId: addresses[0].place_id
                                                        }
                                                    );

                                                    if (
                                                        locationDetail?.coordinates?.lat &&
                                                        locationDetail?.coordinates?.lng
                                                    ) {
                                                        setMapZoom(12);
                                                        setMapViewCenter({
                                                            lat: locationDetail.coordinates.lat,
                                                            lng: locationDetail.coordinates.lng
                                                        });
                                                    }
                                                } else {
                                                    const selectedCity = cityLocations.find(
                                                        city => city.value === values.city
                                                    );
                                                    if (selectedCity && selectedCity.center) {
                                                        setMapZoom(12);
                                                        setMapViewCenter({
                                                            lat: selectedCity.center.lat,
                                                            lng: selectedCity.center.lng
                                                        });
                                                    }
                                                }
                                            } catch (error) {
                                                const selectedCity = cityLocations.find(
                                                    city => city.value === values.city
                                                );
                                                if (selectedCity && selectedCity.center) {
                                                    setMapZoom(12);
                                                    setMapViewCenter({
                                                        lat: selectedCity.center.lat,
                                                        lng: selectedCity.center.lng
                                                    });
                                                }
                                            }
                                        } else if (!countyValue) {
                                            setMapViewCenter(null);
                                        }
                                    }}
                                    selectedCityName={values.city}
                                    optionMappingKey="name"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {renderTextArea("address", "address", "", true)}
                    <VFlex justify="space-between">
                        <VFlex vertical>{t("homeLocation", {ns: "newCourier"})}</VFlex>
                        <VFlex vertical>
                            {`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.homeLocation?.latitude?.toFixed(5) || 41.0828}`}
                            <span style={{marginLeft: "1em"}}>
                                {`${t("fields.longitude", {
                                    ns: "common"
                                })}: ${values.homeLocation?.longitude?.toFixed(5) || 29.0275}`}
                            </span>
                        </VFlex>
                    </VFlex>
                    <div
                        style={
                            errors.homeLocation && touched.homeLocation
                                ? {border: "1px solid #ff4d4f", borderRadius: 2}
                                : undefined
                        }
                        data-testid="homeLocationInput"
                    >
                        <SingleMarkerMap
                            onClick={({lat, lng}) => {
                                const c = {
                                    longitude: lng,
                                    latitude: lat
                                };
                                setFieldValue(`homeLocation`, c);
                                setMapViewCenter(null);
                            }}
                            center={{
                                lat: values?.homeLocation?.latitude || 41.0828,
                                lng: values?.homeLocation?.longitude || 29.0275
                            }}
                            viewCenter={mapViewCenter}
                            zoom={mapZoom}
                            icon={<HomeOutlined />}
                            customButton={
                                <Tooltip
                                    title={t("actions.geolocateCourierHomeLocation", {
                                        ns: "newCourier"
                                    })}
                                >
                                    <GeolocateButton
                                        loading={geolocationLoading}
                                        icon={<AimOutlined />}
                                        size="large"
                                        onClick={async e => await geolocateCourierHomeLocation(e)}
                                    />
                                </Tooltip>
                            }
                        />
                    </div>
                    {errors.homeLocation && touched.homeLocation && (
                        <div style={{color: "#ff4d4f", marginTop: 4}}>
                            Lütfen harita üzerinden ev konumunu seçin
                        </div>
                    )}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderTextArea(
                                "vehicleInfo",
                                "vehicleInfo",
                                t("vehicleInfoPlaceholder", {ns: "newCourier"}),
                                true
                            )}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderLicensePlateInput("licensePlate", "licensePlate")}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            {renderFormInput(
                                "emergencyContactName",
                                "emergencyContactName",
                                7,
                                true
                            )}
                        </Col>
                        <Col xs={24} md={8}>
                            {renderMaskedInput(
                                "emergencyContactPhone",
                                "emergencyContactPhone",
                                7,
                                true
                            )}
                        </Col>
                        <Col xs={24} md={8}>
                            {renderFormInput(
                                "emergencyContactDegree",
                                "emergencyContactDegree",
                                7,
                                true
                            )}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormInput("bankName", "bankName", 11, true)}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderFormInput("iban", "iban", 11, true)}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormInput("taxNumber", "taxNumber", 11, false)}
                        </Col>
                        <Col xs={24} md={12}>
                            {renderFormInput("taxOffice", "taxOffice", 11, false)}
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            {renderFormInput("Şahıs Şirketi Adı", "personCompany", 11, false)}
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Araç Sahibi"
                                required={false}
                                data-testid="vehicleOwnershipSelect"
                            >
                                <Select
                                    name="vehicleOwnership"
                                    value={values.vehicleOwnership}
                                    onChange={value => setFieldValue("vehicleOwnership", value)}
                                >
                                    {vehicleOwnershipTypes.map(type => (
                                        <Select.Option
                                            key={type.value}
                                            value={type.value}
                                            data-testid="vehicleOwnershipSelect_option"
                                        >
                                            {type.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="ÖSS Numarası"
                                //required={true}
                                validateStatus={
                                    errors.ossNumber && touched.ossNumber ? "error" : "success"
                                }
                                help={
                                    errors.ossNumber && touched.ossNumber ? errors.ossNumber : null
                                }
                                data-testid="ossNumberInput"
                                rules={[
                                    {required: true, message: t("Zorunlu Alan")},
                                    {
                                        pattern: /^\d+$/,
                                        message: t("Sadece rakam girebilirsiniz")
                                    },
                                    {min: 3, message: t("En az 3 karakter olmalı")},
                                    {max: 15, message: t("En fazla 15 karakter olmalı")}
                                ]}
                            >
                                <Input
                                    name="ossNumber"
                                    value={values.ossNumber}
                                    onChange={e => setFieldValue("ossNumber", e.target.value)}
                                    maxLength={15}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="ÖSS Başlangıç Tarihi"
                                //required={true}
                                validateStatus={
                                    errors.ossStartDate && touched.ossStartDate
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.ossStartDate && touched.ossStartDate
                                        ? errors.ossStartDate
                                        : null
                                }
                                data-testid="ossStartDateInput"
                            >
                                <DatePicker
                                    name="ossStartDate"
                                    value={values.ossStartDate ? moment(values.ossStartDate) : ""}
                                    onChange={date => setFieldValue("ossStartDate", date)}
                                    placeholder={"ÖSS Başlangıç Tarihi Giriniz"}
                                    format="DD.MM.YYYY"
                                    disabledDate={current => {
                                        return (
                                            !current ||
                                            current.isAfter(moment()) ||
                                            current.isBefore(moment("1950-01-01"))
                                        );
                                    }}
                                    locale={locale}
                                    data-testid="ossStartDatePicker"
                                    style={{width: "100%"}}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Çanta Ruhsata Kayıtlı mı?"
                                required={false}
                                data-testid="hasBagRegisteredSelect"
                            >
                                <Select
                                    name="hasBagRegistered"
                                    value={values.hasBagRegistered}
                                    onChange={value => setFieldValue("hasBagRegistered", value)}
                                >
                                    <Select.Option
                                        key="true"
                                        value={true}
                                        data-testid="hasBagRegisteredSelect_option"
                                    >
                                        Evet
                                    </Select.Option>
                                    <Select.Option
                                        key="false"
                                        value={false}
                                        data-testid="hasBagRegisteredSelect_option"
                                    >
                                        Hayır
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Mesleki Yeterlilik Belgesi"
                                required={false}
                                data-testid="hasProfessionalCompetenceCertificateSelect"
                            >
                                <Select
                                    name="hasProfessionalCompetenceCertificate"
                                    value={values.hasProfessionalCompetenceCertificate}
                                    onChange={value =>
                                        setFieldValue("hasProfessionalCompetenceCertificate", value)
                                    }
                                >
                                    <Select.Option
                                        key="true"
                                        value={true}
                                        data-testid="hasProfessionalCompetenceCertificateSelect_option"
                                    >
                                        Evet
                                    </Select.Option>
                                    <Select.Option
                                        key="false"
                                        value={false}
                                        data-testid="hasProfessionalCompetenceCertificateSelect_option"
                                    >
                                        Hayır
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Çalışma Şekli" data-testid="employmentTypeSelect">
                                <Select
                                    name="employmentType"
                                    value={values.employmentType}
                                    onChange={value => setFieldValue("employmentType", value)}
                                    allowClear
                                    placeholder="Seçiniz"
                                >
                                    {courierEmploymentTypes.map(item => (
                                        <Select.Option key={item.value} value={item.value}>
                                            {item.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Çalışma Zamanı" data-testid="workingModelIdSelect">
                                <Select
                                    name="workingModelId"
                                    value={values.workingModelId}
                                    onChange={value =>
                                        setFieldValue("workingModelId", value ?? undefined)
                                    }
                                    allowClear
                                    showSearch
                                    loading={workingModelsLoading}
                                    placeholder="Seçiniz"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.children ?? "")
                                            .toString()
                                            .toLowerCase()
                                            .indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {workingModels.map(option => (
                                        <Select.Option key={option._id} value={option._id}>
                                            {option?.label?.tr}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Yönetici Firma"
                                required={
                                    !(
                                        values.activationStep === "candidate" &&
                                        [
                                            "none",
                                            "companySetup",
                                            "newApplication",
                                            "document"
                                        ].includes(values.activationPhase)
                                    )
                                }
                                validateStatus={
                                    errors.managerCompany && touched.managerCompany
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.managerCompany && touched.managerCompany
                                        ? errors.managerCompany
                                        : null
                                }
                                data-testid="managerCompanySelect"
                            >
                                <Select
                                    name="managerCompany"
                                    value={values.managerCompany}
                                    onChange={value => setFieldValue("managerCompany", value)}
                                    onBlur={() => setFieldTouched("managerCompany", true)}
                                    placeholder="Seçiniz"
                                >
                                    {managerCompanyOptions.map(item => (
                                        <Select.Option key={item.value} value={item.value}>
                                            {item.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Sağlayıcı Firma"
                                        data-testid="providerCompanySelect"
                                    >
                                        <Select
                                            name="providerCompany"
                                            value={values.providerCompany}
                                            onChange={value =>
                                                setFieldValue("providerCompany", value)
                                            }
                                            disabled={!canEditProviderCompany}
                                            style={{width: "100%"}}
                                        >
                                            {providerCompanies.map(item => (
                                                <Select.Option key={item.value} value={item.value}>
                                                    {item.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Sağlayıcı Firmaya Geçiş Tarihi"
                                        required={true}
                                        validateStatus={
                                            errors.vionTransitionDate && touched.vionTransitionDate
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors.vionTransitionDate && touched.vionTransitionDate
                                                ? errors.vionTransitionDate
                                                : null
                                        }
                                        data-testid="vionTransitionDateInput"
                                    >
                                        <DatePicker
                                            name="vionTransitionDate"
                                            value={
                                                values.vionTransitionDate
                                                    ? moment(values.vionTransitionDate)
                                                    : ""
                                            }
                                            onChange={date =>
                                                setFieldValue("vionTransitionDate", date)
                                            }
                                            placeholder={"Sağlayıcı Firmaya Geçiş Tarihi Giriniz"}
                                            format="DD.MM.YYYY"
                                            locale={locale}
                                            disabled={!canEditProviderCompany}
                                            data-testid="vionTransitionDatePicker"
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="MH Numarası"
                                //required={true}
                                validateStatus={
                                    errors.mhNumber && touched.mhNumber ? "error" : "success"
                                }
                                help={errors.mhNumber && touched.mhNumber ? errors.mhNumber : null}
                                data-testid="mhNumberInput"
                            >
                                <Input
                                    name="mhNumber"
                                    value={values.mhNumber}
                                    onChange={e => setFieldValue("mhNumber", e.target.value)}
                                    maxLength={15}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col
                            xs={24}
                            md={12}
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center"
                            }}
                        >
                            {renderCheckbox(criminalCheck, setCriminalCheck, "criminalCheckText")}
                        </Col>
                        <Col
                            xs={24}
                            md={12}
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center"
                            }}
                        >
                            <VFlex vertical>
                                <ConditionalTooltip
                                    text={t("criminalCheckTooltip", {ns: "newCourier"})}
                                    condition={!criminalCheck}
                                    child={
                                        <Popconfirm
                                            visible={hakedisConfirmVisible}
                                            title={checkMessage}
                                            okText="Kaydet"
                                            cancelText="İptal Et"
                                            onConfirm={() => {
                                                setHakedisConfirmVisible(false);
                                                runProviderUpdate();
                                            }}
                                            onCancel={() => setHakedisConfirmVisible(false)}
                                        >
                                            <CustomButton
                                                type="primary"
                                                htmlType="button"
                                                data-testid="submitButton"
                                                disabled={isSubmitting}
                                                onClick={handleSaveClick}
                                            >
                                                {t("actions.save", {ns: "common"})}
                                            </CustomButton>
                                        </Popconfirm>
                                    }
                                />
                            </VFlex>
                        </Col>
                    </Row>
                </Form>
            </VFlex>
        </VFlex>
    ));
};
